import { useTheme } from "@shopify/restyle";
import React from "react";
import { Platform } from "react-native";
import { Svg, SvgProps } from "react-native-svg";

import { Theme } from "../../../theme/restyle/theme";
import { RestyleColorString } from "../../../theme/types";

export interface IconProps extends Omit<SvgProps, "color"> {
  viewBox?: string;
  color?: keyof Theme["colors"] | RestyleColorString | string;
  title?: string;
}

export const Icon: React.FC<IconProps & { children: React.ReactNode }> = ({
  children,
  color = "outline-primary",
  title,
  accessibilityLabel,
  accessible,
  ...props
}) => {
  const theme = useTheme<Theme>();

  // Convert color token to hex value, or use as-is if it's already a hex
  const colorValue: string | undefined =
    color && theme.colors[color as keyof Theme["colors"]]
      ? theme.colors[color as keyof Theme["colors"]]
      : color?.toString() || "currentColor";

  // When the icon is explicitly hidden from accessibility (e.g. decorative inside
  // an accessible button), suppress the label so screen readers and RNTL queries
  // do not find a duplicate element.
  const resolvedAccessibilityLabel =
    accessible === false ? undefined : accessibilityLabel ?? title;
  const accessibilityProps: Partial<SvgProps> =
    Platform.OS === "web"
      ? {
          "aria-hidden": accessible === false ? true : undefined,
          "aria-label": resolvedAccessibilityLabel,
          role: resolvedAccessibilityLabel ? "img" : undefined,
        }
      : {
          accessibilityLabel: resolvedAccessibilityLabel,
          accessibilityRole: "image" as const,
          accessible: accessible ?? !!resolvedAccessibilityLabel,
        };

  return (
    <Svg
      viewBox="0 0 24 24"
      width={props.width || 16}
      height={props.height || 16}
      color={colorValue}
      title={title}
      {...accessibilityProps}
      {...props}
    >
      {children}
    </Svg>
  );
};

export default Icon;
